import React, { createContext, type PropsWithChildren, useContext, useState } from 'react';
import { DataTableProps, DataTableTypes } from '../../DataTableTypes';
import { dataTableDefaultFeatures, dataTableDefaultProps } from '../const/defaultValues';

interface DataTableContextState extends Omit<DataTableTypes.DerivedState<any>, 'state'> {
  tableInstance: DataTableTypes.TableInstance<any>;
  state: DataTableTypes.DerivedState<DataTableTypes.QueryModel>['state'] & {
    selectedRows: DataTableTypes.SelectedRow<any>[];
    filterDrawerOpen: boolean;
  };
  actions: {
    setPageNumber(page: number): void;
    setModelChange(nextModel: DeepPartial<DataTableTypes.TableModel<DataTableTypes.QueryModel>>): void;
    setSortChange(type: DataTableTypes.SortModel, wasSet: boolean, wasReversed: boolean): void;
    setColumnConfigurationChange(configuration: DataTableTypes.ColumnConfiguration<any>, debounce?: boolean): void;
    setFilterDrawerOpen(updater: boolean | ((open: boolean) => boolean)): void;
  };
  model: DataTableTypes.TableModel<DataTableTypes.QueryModel>;
  slots?: DataTableProps<DataTableTypes.QueryModel>['slots'];
  slotProps?: DataTableProps<DataTableTypes.QueryModel>['slotProps'];
}

interface DataTableContextProviderProps {
  tableInstance: DataTableTypes.TableInstance<any>;
  derivedState: DataTableTypes.DerivedState<any>;
  selectedRows: DataTableTypes.SelectedRow<any>[];
  slots: DataTableProps<any>['slots'] | undefined;
  slotProps: DataTableProps<any, any>['slotProps'] | undefined;
  model: DataTableTypes.TableModel<any>;
  setModelChange(nextModel: DeepPartial<DataTableTypes.TableModel<any>>): void;
  setColumnConfigurationChange(configuration: DataTableTypes.ColumnConfiguration<any>): void;
}

const DataTableContext = createContext<DataTableContextState>({
  tableInstance: {} as DataTableTypes.TableInstance<any>,
  ui: {
    variant: 'default',
    show: {
      toolbar: true
    }
  },
  state: {
    loading: dataTableDefaultProps.loading,
    loadingInBackground: dataTableDefaultProps.loading,
    selectedRows: [],
    filterDrawerOpen: false
  },
  features: dataTableDefaultFeatures,
  model: {
    query: {},
    page: {
      limit: dataTableDefaultProps.limit,
      offset: dataTableDefaultProps.offset
    },
    sort: undefined
  },
  pageModel: {
    limit: dataTableDefaultProps.limit,
    offset: dataTableDefaultProps.offset,
    currentPage: 0,
    totalCount: dataTableDefaultProps.totalCount
  },
  actions: {
    setPageNumber: noop,
    setModelChange: noop,
    setColumnConfigurationChange: noop,
    setFilterDrawerOpen: noop,
    setSortChange: noop
  }
});

export const DataTableContextProvider: React.FC<PropsWithChildren<DataTableContextProviderProps>> = ({
  children,
  tableInstance,
  derivedState,
  selectedRows,
  slots,
  slotProps,
  setModelChange,
  setColumnConfigurationChange,
  model
}) => {
  const { pageModel, state, features, ui } = derivedState;
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  return (
    <DataTableContext.Provider
      value={{
        tableInstance,
        pageModel,
        features,
        model,
        slots,
        slotProps,
        ui,
        state: { ...state, filterDrawerOpen, selectedRows },
        actions: {
          setPageNumber: (page: number) => {
            setModelChange({
              ...model,
              page: {
                ...model.page,
                offset: page * model.page.limit
              }
            });
          },
          setModelChange: (nextModel) => {
            setModelChange({
              ...nextModel,
              page: {
                ...nextModel.page,
                offset: 0
              }
            });
          },
          setSortChange(type: DataTableTypes.SortModel, wasSet: boolean, wasReversed: boolean) {
            setModelChange({
              query: model.query,
              page: {
                ...model.page,
                offset: 0
              },
              sort: !wasSet ? [{ type, reversed: false }] : !wasReversed ? [{ type, reversed: true }] : undefined
            });
          },
          setColumnConfigurationChange,
          setFilterDrawerOpen
        }
      }}
    >
      {children}
    </DataTableContext.Provider>
  );
};

export function useDataTableContext() {
  return useContext(DataTableContext);
}

function noop() {}
